import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import ConfirmDialog from "../../components/admin/ConfirmDialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Card } from "../../components/ui/card";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import PageHeader from "../../components/admin/PageHeader";
import InfoBanner from "../../components/admin/InfoBanner";
import FilterBar from "../../components/admin/FilterBar";

const BASE_URI = process.env.REACT_APP_API_BASE_URI;
const emptyForm = { day: "", month_year: "", message: "", is_new: false };

const NoticeCRUD = () => {
  const [notices, setNotices] = useState([]);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [editNotice, setEditNotice] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState("");
  const [pagination, setPagination] = useState({ page: 1, page_size: 20, total: 0, total_pages: 1 });

  const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchNotices(); }, []);

  const fetchNotices = async (page = 1) => {
    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URI}/entities?category=notice&page=${page}&limit=${pagination.page_size}`, getAuthHeaders());
      if (res.data?.data) {
        setNotices(res.data.data.map((i) => ({ id: i.id, day: i.data.day || "", month_year: i.data.month_year || "", message: i.data.message || "", is_new: i.data.is_new || false })));
        setPagination({ page: res.data.page, page_size: res.data.limit, total: res.data.total, total_pages: Math.ceil(res.data.total / res.data.limit) });
      }
      setError("");
    } catch { setError("Failed to fetch notices"); }
    finally { setLoading(false); }
  };

  const openAdd = () => { setForm(emptyForm); setEditNotice(null); setDialogOpen(true); };
  const openEdit = (n) => { setForm({ day: n.day, month_year: n.month_year, message: n.message, is_new: n.is_new }); setEditNotice(n); setDialogOpen(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.day.trim() || !form.month_year.trim() || !form.message.trim()) { toast.warning("Please fill all required fields"); return; }
    try {
      setLoading(true);
      const fd = new FormData();
      fd.append("day", form.day); fd.append("month_year", form.month_year);
      fd.append("message", form.message); fd.append("is_new", form.is_new);
      const hdrs = { headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${localStorage.getItem("token")}` } };
      if (editNotice) {
        await axios.put(`${BASE_URI}/entities/${editNotice.id}?category=notice`, fd, hdrs);
        toast.success("Notice updated!");
      } else {
        await axios.post(`${BASE_URI}/entities?category=notice`, fd, hdrs);
        toast.success("Notice created!");
      }
      setDialogOpen(false); fetchNotices(pagination.page);
    } catch { toast.error("Save failed"); }
    finally { setLoading(false); }
  };

  const deleteNotice = async () => {
    try {
      setDeleteLoading(true);
      await axios.delete(`${BASE_URI}/entities/${deleteId}?category=notice`, getAuthHeaders());
      toast.success("Deleted successfully!");
      setDeleteId(null);
      fetchNotices(notices.length === 1 && pagination.page > 1 ? pagination.page - 1 : pagination.page);
    } catch { toast.error("Failed to delete."); }
    finally { setDeleteLoading(false); }
  };

  const filtered = notices.filter(n => n.message.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-6">
      <PageHeader
        title="Notice Board"
        description="Manage notices and announcements displayed on the website"
        action={<Button onClick={openAdd}><Plus className="h-4 w-4" /> Add Notice</Button>}
      />
      <InfoBanner
        title="Notice Management"
        description="Post important announcements, deadlines, and updates for students and visitors."
        capabilities={["Mark notices as new", "Date-stamped entries", "Paginated notice list"]}
      />
      <FilterBar search={search} onSearch={setSearch} placeholder="Search by message…" count={filtered.length} />
      {error && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[100px]">Date</TableHead>
              <TableHead className="w-[80px]">Status</TableHead>
              <TableHead>Message</TableHead>
              <TableHead className="w-[120px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={4} className="text-center py-12 text-gray-400">
                <div className="flex items-center justify-center gap-2"><div className="h-4 w-4 rounded-full border-2 border-blue-400 border-t-transparent animate-spin" />Loading…</div>
              </TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={4} className="text-center py-12 text-gray-400">No notices yet.</TableCell></TableRow>
            ) : filtered.map((n) => (
              <TableRow key={n.id}>
                <TableCell className="align-top">
                  <div className="font-bold text-gray-900">{n.day}</div>
                  <div className="text-xs text-gray-500">{n.month_year}</div>
                </TableCell>
                <TableCell className="align-top">
                  <Badge variant={n.is_new ? "new" : "secondary"}>{n.is_new ? "New" : "Old"}</Badge>
                </TableCell>
                <TableCell className="text-gray-600 text-sm align-top">
                  <p className="line-clamp-2">{n.message}</p>
                </TableCell>
                <TableCell className="text-right align-top">
                  <div className="flex items-center justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEdit(n)}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant="destructive" onClick={() => setDeleteId(n.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-center gap-1 p-4 border-t border-gray-100">
            <Button size="sm" variant="outline" onClick={() => fetchNotices(pagination.page - 1)} disabled={pagination.page === 1 || loading}>Previous</Button>
            {Array.from({ length: pagination.total_pages }, (_, i) => i + 1).map((p) => (
              <Button key={p} size="sm" variant={p === pagination.page ? "default" : "outline"} onClick={() => fetchNotices(p)} disabled={loading}>{p}</Button>
            ))}
            <Button size="sm" variant="outline" onClick={() => fetchNotices(pagination.page + 1)} disabled={pagination.page === pagination.total_pages || loading}>Next</Button>
          </div>
        )}
      </Card>

      <ConfirmDialog
        open={!!deleteId}
        title="Delete notice?"
        description="This action cannot be undone."
        onConfirm={deleteNotice}
        onCancel={() => setDeleteId(null)}
        loading={deleteLoading}
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editNotice ? "Edit Notice" : "Add New Notice"}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 px-6 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Day <span className="text-red-500">*</span></Label>
                <Input placeholder="e.g. 15" value={form.day} onChange={(e) => setForm({ ...form, day: e.target.value })} required />
              </div>
              <div className="space-y-1.5">
                <Label>Month &amp; Year <span className="text-red-500">*</span></Label>
                <Input placeholder="e.g. August 2025" value={form.month_year} onChange={(e) => setForm({ ...form, month_year: e.target.value })} required />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Message <span className="text-red-500">*</span></Label>
              <Textarea placeholder="Notice content…" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input type="checkbox" checked={form.is_new} onChange={(e) => setForm({ ...form, is_new: e.target.checked })} className="rounded border-gray-300" />
              Mark as New Notice
            </label>
          </form>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={loading}>{loading ? "Saving…" : editNotice ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NoticeCRUD;
